import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from './../../redux/AppReducer';
import { useEffect } from 'react';
import { ajax } from 'rxjs/ajax';
import { mergeMap } from 'rxjs';
//components
import Navbar from './Navbar';
import Header from './Header';
import Footer from './Footer';
import './index.scss';
import { GeneralErrorComponent } from '../../components/GeneralError/GeneralErrorComponent';
import { PopUpComponent } from '../UI/PopUpComponent/PopUpComponent';
import { useWindowSize } from '../../hooks/useWindowSize';
import { ScreenActions } from '../../redux/ScreenActions';
import {
	Filter,
	FilterType,
	FilterVM,
	ICategoryNavbar,
	ScreenType,
	TrackingEventStatus
} from '../../common/view-model';
import { Outlet, useNavigate } from 'react-router';
import { SearchActions } from '../../redux/search/SearchActions';
import { NavbarCategoryComponent } from '../filter/navbar-category/NavbarCategoryComponent';
import { FilterActions } from '../../redux/FilterActions';
import { handleTracking } from '../../helpers/handelTracking';
import { v4 as uuidv4 } from 'uuid';
import { flatFirstLevelFilters } from '../../helpers/urlParametersHelpers';
import {
	getFilterTypeKeyForUrl,
	buildUrlParameters,
	flatThirdLevelChildren
} from '../../helpers/urlParametersHelpers';
import {
	secondLevelCategoryIdentifier,
	firstLevelCategoryIdentifier,
	flatLevelCategoryIdentifier,
	parentCategoryIdentifier
} from '../../helpers/paramsUrlIndetifiers';
import { AppActions } from '../../redux/AppActions';
import { UserActions } from '../../redux/UserActions';
import { lastUrlLocalStorageKey, allQuestionsIds } from '../../common/constants';
import { useAppDispatch } from '../../redux/store';
import LockOverlayComponent from '../LockOverlay/LockOverlayComponent';

interface LayoutProps {
	isAuthenticated?: boolean;
	children?: any;
}

const Layout = ({ isAuthenticated }: LayoutProps) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { width } = useWindowSize();

	const clearError = () => dispatch(AppActions.setGeneralError({ hasError: false }));
	const setDeviceScreen = (screenWidth: number) =>
		dispatch(ScreenActions.setScreenDevice(screenWidth));
	const seHasClickedOutsideSearch = (clicked: boolean) =>
		dispatch(SearchActions.seHasClickedOutsideSearch(clicked));
	const mapCategoryNavbarElements = () => dispatch(FilterActions.mapNavbarElements());
	const setCloseOverlay = (setCurrentCategory: () => void) =>
		dispatch(FilterActions.addOnMouseLeaveCategoryNavbar(setCurrentCategory));
	const addFilter = (filter: Filter) => dispatch(FilterActions.addExternalfilter(filter));
	const addBusinessFilter = (filter: Filter) => dispatch(FilterActions.addBusinessFilter(filter));
	const removeAllFilters = () => dispatch(FilterActions.clearAllFilters());
	const clearSearchTerm = () => dispatch(SearchActions.setSearchTerm(undefined));
	const clearAllFiltersAndSearchTerm = () => dispatch(FilterActions.clearAllFiltersAndSearchTerm());
	const registerUserClickEvent = (event: string) =>
		dispatch(UserActions.registerUserClickEvent(event));
	const standAloneLayout = useSelector(({ appState: state }: RootState) => state.standAloneLayout);
	const isProfileLayout = useSelector(({ appState: state }: RootState) => state.isProfileLayout);
	const errorState = useSelector(({ appState: state }: RootState) => state.errorState);
	const modalContent = useSelector(({ appState: state }: RootState) => state.modalContent);
	const deviceScreen = useSelector(({ appState: state }: RootState) => state.deviceScreen);
	const onFilterPanelOutsiteClick = useSelector(
		({ appState: state }: RootState) => state.filterState?.onFilterPanelOutsiteClick
	);
	const showNavbar = useSelector(({ appState: state }: RootState) => state.showNavbar);
	const isCompanyLoading = useSelector(({ appState: state }: RootState) => state.isCompanyLoading);
	const hasMappedAllFilters = useSelector(
		({ appState: state }: RootState) => state.filterState?.hasMappedAllFilters
	);
	const currentEvent = useSelector(
		({ appState: state }: RootState) => state.storeState?.getCurrentEvent
	);
	const navbarCategoryElements = useSelector(
		({ appState: state }: RootState) => state.filterState?.categoryNavbarElements
	);
	const userState = useSelector(({ appState: state }: RootState) => state.userState);
	const homePageLayout = useSelector(({ appState: state }: RootState) => state.homePageLayout);
	const hasClickedOutsideBar = useSelector(
		({ appState: state }: RootState) => state.searchState?.hasClickedOutsideBar || false
	);
	const loadProfileProgress = useSelector(
		({ appState: state }: RootState) => state.loadProfileProgress
	);
	const allCategories = useSelector(
		({ appState: state }: RootState) =>
			state.filterState?.allFilters?.find(filter => filter.type === FilterType.CATEGORY)?.filters
	);
	const hasAnonymousEnded = useSelector(
		({ appState: state }: RootState) => state.userState?.hasAnonymousEnded
	);

	const homePageUrl = 'products';
	const overlayStyle = { backgroundColor: 'rgba(0,0,0,.4)' };

	const [categoryTab, setCategoryTab] = useState<ICategoryNavbar | undefined>(undefined);
	const [isOnHomePage, setIsOnHomePage] = useState<boolean>(
		window.location.pathname.includes(homePageUrl) || deviceScreen !== ScreenType.TABLET
	);

	const handleLayoutClick = (e: any) => {
		const targetIdToBeClose = 'popup-overlay';
		const targetClass: string = e.target.className.toString();
		if (targetClass.toLowerCase().trim() === targetIdToBeClose.toLowerCase().trim()) {
			onFilterPanelOutsiteClick && onFilterPanelOutsiteClick();
		}
	};
	const handleAddFilterFromNavbar = useCallback(
		(filters: FilterVM[]) => {
			clearError();
			clearSearchTerm();
			removeAllFilters();
			setCategoryTab(undefined);
			filters.forEach(filter => {
				if (!filter) return;
				addFilter(filter);
				handleTracking({
					transactionId: uuidv4(),
					entityId: filter.id!,
					status: TrackingEventStatus.SUCCESS,
					type: `Nav Click on ${filter.name}`,
					timeStamp: new Date().toISOString(),
					eventPayload: {
						id: filter.id!,
						name: filter.name!,
						type: getFilterTypeKeyForUrl(filter.type!),
						url: window.location.href,
						tags: [],
						metadata: {
							user_id: userState?.id,
							user_type: userState?.user_type,
							environment: import.meta.env.VITE_ENV_NAME,
							filter_id: filter.id,
							filter_name: filter.name
						}
					}
				});
			});
			const urlParameters = buildUrlParameters(filters);
			navigate(`/${homePageUrl}?${urlParameters}`);
		},
		[navigate, userState?.user?.business_email, userState?.user?.user_type]
	);
	const handleBizFilterFromNavbar = useCallback((filter: any) => {
		clearError();
		clearSearchTerm();
		setCategoryTab(undefined);
		addBusinessFilter(filter);
		if (filter.id) {
			const urlParameters = `?category=${filter.id}`;
			navigate(`/${'businessProviders'}${urlParameters}`);
		} else {
			navigate(`/${'businessProviders'}`);
		}
	}, []);
	const handleAddCategoryFilter = useCallback(
		(filter: FilterVM) => {
			const isSuperCategory = filter.isSuperCategory;
			const flattenThirdLevel = flatThirdLevelChildren(allCategories);
			const firstLevelFilters = flatFirstLevelFilters(allCategories);

			const thirdLevelFilters = flattenThirdLevel.flatMap((child: FilterVM) =>
				child.children ? child.children : []
			);

			const tempFilter: FilterVM = {
				...filter,
				children: thirdLevelFilters.find(child => child.id === filter.id)?.children || []
			};

			clearAllFiltersAndSearchTerm();
			setCategoryTab(undefined);

			if (filter.name?.toLowerCase() === 'see all products') {
				navigate('/products');
				return;
			}
			const eventPayload = {
				id: tempFilter.id!,
				name: tempFilter.name!,
				type: getFilterTypeKeyForUrl(tempFilter.type!),
				url: window.location.href,
				tags: [],
				metadata: {
					user_id: userState?.id,
					user_type: userState?.user_type,
					environment: import.meta.env.VITE_ENV_NAME,
					filter_id: tempFilter.id,
					filter_name: tempFilter.name
				}
			};

			handleTracking({
				transactionId: uuidv4(),
				entityId: tempFilter.id!,
				status: TrackingEventStatus.SUCCESS,
				type: `Nav Click on ${filter.name}`,
				timeStamp: new Date().toISOString(),
				eventPayload
			});

			const childrenIds = tempFilter.children?.map((child: FilterVM) => child.id) || [];
			addFilter(tempFilter);
			let queryParams = '';
			const secondLevelIdentifier = firstLevelFilters.find(f => f.id === tempFilter.id)?.parentId;
			if (isSuperCategory) {
				queryParams = `?${parentCategoryIdentifier}=${tempFilter.parentId}&${secondLevelCategoryIdentifier}=${tempFilter.id}`;
			} else {
				if (!tempFilter.hasBasedLevelFilters) {
					queryParams = `?${parentCategoryIdentifier}=${
						tempFilter.parentId
					}&${secondLevelCategoryIdentifier}=${secondLevelIdentifier}&${firstLevelCategoryIdentifier}=${
						tempFilter.id
					}${
						childrenIds.length > 0 ? `&${flatLevelCategoryIdentifier}=${childrenIds.join(',')}` : ''
					}`;
				} else {
					const secondLevelIdentifier = firstLevelFilters.find(
						f => f.id === tempFilter.parentId
					)?.parentId;
					queryParams = `?${parentCategoryIdentifier}=${tempFilter.parentFilterId}&${secondLevelCategoryIdentifier}=${secondLevelIdentifier}&${flatLevelCategoryIdentifier}=${tempFilter.id}`;
				}
			}
			clearError();
			navigate(`/${homePageUrl}${queryParams}`);
		},
		[allCategories, navigate, userState?.user?.business_email, userState?.user?.user_type]
	);

	useEffect(() => {
		const subscription = ajax({
			//url : "https://api-dev.iiris.com/v1/admin/public/config/item/f5701c3323cacc67c4aa16d853083cf8",
			url: `${import.meta.env.VITE_IRIS_DOMAIN}/v1/admin/public/config/item/${
				import.meta.env.VITE_PASSPORT_CONFIG
			}`,
			method: 'GET',
			headers: { 'Content-Type': 'application/json' }
		})
			.pipe(
				mergeMap(result => {
					const response: any = result.response;
					const questions = response?.config?.widget_configs?.[0]?.pages?.[0]?.questions;
					const questionsIds: any[] = [];
					questions.forEach((q: { id: any }) => {
						questionsIds.push(q.id);
					});
					Object.assign(allQuestionsIds, questionsIds);
					return [];
				})
			)
			.subscribe();

		return () => subscription.unsubscribe();
	}, []);

	useEffect(() => {
		width > 0 && setDeviceScreen(width);
	}, [width]);

	useEffect(() => {
		setIsOnHomePage(
			(window.location.pathname.includes(homePageUrl) &&
				!window.location.pathname.includes('/video')) ||
				(deviceScreen !== ScreenType.TABLET && !window.location.pathname.includes('/video'))
		);
	}, [window.location.pathname, deviceScreen]);

	useEffect(() => {
		if (hasMappedAllFilters && navbarCategoryElements?.length === 0 && currentEvent !== undefined) {
			mapCategoryNavbarElements();
		}
	}, [currentEvent, hasMappedAllFilters, navbarCategoryElements]);

	useEffect(() => {
		if (
			isAuthenticated &&
			userState?.hasLoadProfile &&
			window.location.pathname === '/landing' &&
			loadProfileProgress === 100
		) {
			navigate(localStorage.getItem(lastUrlLocalStorageKey) || '/');
			localStorage.removeItem(lastUrlLocalStorageKey);
		}
	}, [isAuthenticated, userState?.hasLoadProfile, userState, loadProfileProgress, navigate]);

	useEffect(() => {
		if (isAuthenticated === false && hasAnonymousEnded === true) {
			if (location.pathname !== '/') {
				navigate('/');
			}
			dispatch(
				AppActions.setModalContent({
					content: <LockOverlayComponent />,
					showCloseIcon: false,
					canScroll: false,
					closeOnDocumentClick: false
				})
			);
		}
	}, [isAuthenticated, hasAnonymousEnded]);

	return (
		<div data-testid='layout-container' onClick={handleLayoutClick}>
			<div onClick={() => seHasClickedOutsideSearch(true)}>
				<Header />
			</div>
			<>
				{!standAloneLayout ? (
					isProfileLayout ? (
						<>
							<Navbar />
							<div className='standalone-content'>
								{errorState?.hasError || <Outlet />}
								<GeneralErrorComponent open={errorState?.hasError || false} error={errorState} />
							</div>
							<Footer />
						</>
					) : (
						<>
							<Navbar />
							{navbarCategoryElements && navbarCategoryElements.length > 0 && (
								<NavbarCategoryComponent
									handleAddCategoryFilter={handleAddCategoryFilter}
									navbarCategoryElements={navbarCategoryElements}
									categoryTab={categoryTab}
									setCategoryTab={setCategoryTab}
									setCloseOverlay={setCloseOverlay}
									handleAddFilterFromNavbar={handleAddFilterFromNavbar}
									handleAddBizFromNavbar={handleBizFilterFromNavbar}
									registerUserClickEvent={registerUserClickEvent}
									hasClickedOutsideBar={hasClickedOutsideBar}
									eventName={currentEvent?.name || ''}
									user={userState}
									productsPageUrl={`/${homePageUrl}`}
									allCategories={allCategories || []}
								/>
							)}

							<div className='grid-container' onClick={() => seHasClickedOutsideSearch(true)}>
								<div
									className={
										isOnHomePage && deviceScreen !== ScreenType.TABLET
											? !homePageLayout
												? 'main-content'
												: 'main-content-homepage'
											: 'secondary-layout'
									}
								>
									{errorState?.hasError || <Outlet />}
									<GeneralErrorComponent open={errorState?.hasError || false} error={errorState} />
								</div>
								<Footer />
							</div>
						</>
					)
				) : (
					<>
						{showNavbar && <Navbar />}
						<div className='standalone-content'>
							<Outlet />
						</div>
						<Footer />
					</>
				)}
			</>
			{modalContent && (
				<PopUpComponent
					open={modalContent !== undefined}
					content={modalContent.content}
					IsModal={true}
					overlayStyle={overlayStyle}
					shouldLockScroll={!modalContent.canScroll}
					closeOnDocumentClick={modalContent.closeOnDocumentClick}
					showCloseIcon={modalContent.showCloseIcon && !isCompanyLoading}
					isCompanyDetailsModal={modalContent.isCompanyDetailsModal}
				/>
			)}
		</div>
	);
};

export default Layout;
